import { FC, useMemo, useState } from 'react';
import { isEmailValid } from '../../../utils';
import {
    Button,
    ButtonLink,
    ButtonSizes,
    ButtonStyles,
    EntityChip,
    Icon,
    Icons,
    Label,
    TextInput,
    Tooltip
} from '@silverfort/sf-components';
import { Collapse } from '@material-ui/core';

const RecipientsList: FC<{
    recipients: string[];
    setRecipients: (recipients: string[]) => void;
}> = ({ recipients, setRecipients }) => {
    const [emailsInput, setEmailsInput] = useState('');
    const [validationMessage, setValidationMessage] = useState('');

    const normalizedEmails = useMemo(() => recipients.map((email) => email.toLowerCase()), [recipients]);

    const validateEmailInput = (email) => {
        if (!isEmailValid(email)) return 'Invalid email address';
        const normalizedEmail = email.toLowerCase();
        if (normalizedEmails.includes(normalizedEmail)) return 'Email already exists';
    };

    const validateEmailInList = (email) => {
        if (!isEmailValid(email)) return 'Invalid email addresses found';
        const normalizedEmail = email.toLowerCase();
        if (normalizedEmails.filter((e) => e === normalizedEmail).length > 1) return 'Duplicate email addresses found';
    };

    const handleInput = () => {
        const inputArray = emailsInput
            .split(/[,\s]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        if (inputArray.length > 1) {
            setEmailsInput('');
            setRecipients([...recipients, ...inputArray]);
        } else if (inputArray.length === 1) {
            const validation = validateEmailInput(inputArray[0]);
            if (validation) {
                setValidationMessage(validation);
            } else {
                setEmailsInput('');
                setRecipients([...recipients, ...inputArray]);
            }
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <Label text="Recipients" />
                <TextInput
                    validationMessage={validationMessage}
                    placeholder="Enter email addresses"
                    value={emailsInput}
                    onChange={(val) => {
                        setValidationMessage('');
                        setEmailsInput(val);
                    }}
                    className="w-full"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleInput();
                        }
                    }}
                />
            </div>
            <Button
                text="Add"
                onClick={handleInput}
                buttonSize={ButtonSizes.Large}
                buttonStyle={ButtonStyles.Primary}
                textSize="text-12"
                disabled={!emailsInput.trim()}
            />
            <Collapse in={!!recipients.length}>
                <div className="flex flex-col border border-sf-gray-100 rounded-lg">
                    <div className="flex text-12 items-center justify-between border-b px-3 py-2">
                        <span className="flex items-center text-sf-gray-500 text-12 gap-1">
                            <Icon icon={Icons.UserGroup} className="h-4 w-4" />
                            {recipients.length}
                        </span>
                        <ButtonLink text="Remove all" onClick={() => setRecipients([])} />
                    </div>
                    <div className="flex flex-wrap items-start content-start gap-1 overflow-y-auto p-2">
                        {recipients.map((recipient, index) => (
                            <div key={index}>
                                <Tooltip title={recipient}>
                                    <EntityChip
                                        icon={Icons.User}
                                        text={recipient.split('@')[0]}
                                        removable
                                        onRemove={() => setRecipients(recipients.filter((_, i) => i !== index))}
                                        invalid={!!validateEmailInList(recipient)}
                                    />
                                </Tooltip>
                            </div>
                        ))}
                    </div>
                </div>
            </Collapse>
        </div>
    );
};

export default RecipientsList;
